import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Ring } from 'react-awesome-spinners';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';
import palette from '../../lib/styles/palette';
import { getAllSales } from '../../lib/api/sales';
import CategoryMap from '../../lib/def/category';
import Chart from './Chart';

// 일별 매출 추이 차트와 동일한 색/포맷 사용 (Sales.js 와 일관)
const COLORS = ['#0ca678', '#1971c2', '#e8590c', '#862e9c', '#2b8a3e', '#c92a2a'];
// 판매 요약 파이 색 (기존 Sales.js 의 Chart 컴포넌트 팔레트와 동일)
const PIE_COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#7FDBFF', '#878BB6', '#3cba9f', '#c45850', '#F5DEB3'];

const formatYAxis = (max) => {
  if (!max || max <= 0) return [0];
  const step = max <= 50000000 ? 10000000 : max <= 200000000 ? 50000000 : 100000000;
  const ticks = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] < max) ticks.push(max);
  return ticks;
};

const formatYLabel = (n) => {
  if (n >= 100000000) return `${(n / 100000000).toFixed(0)}억`;
  if (n >= 10000) return `${(n / 10000).toFixed(0)}만`;
  return n.toLocaleString();
};

const YearSalesModal = ({ visible, onClose, clubID, socketKey, year }) => {
  const [selectedYear, setSelectedYear] = useState(year || moment().year());
  const [loading, setLoading] = useState(false);
  const [yearData, setYearData] = useState(null); // { 카테고리명: [12개월 합계] }

  // 모달이 열릴 때마다 현재 보고 있는 월의 연도로 초기화
  useEffect(() => {
    if (visible) setSelectedYear(year || moment().year());
  }, [visible, year]);

  // 선택 연도의 매출을 1/1~12/31 범위로 1회 조회 후 카테고리×월 집계 (일별 차트와 동일 기준)
  useEffect(() => {
    if (!visible || !clubID) return;
    let canceled = false;
    const fetchYear = async () => {
      setLoading(true);
      try {
        const startTime = parseInt(moment(`${selectedYear}-01-01`).startOf('year').unix());
        const endTime = parseInt(moment(`${selectedYear}-01-01`).endOf('year').unix());
        const res = await getAllSales({ clubID, socketKey, startTime, endTime });
        const infos = (res && res.data && res.data.infos) || [];

        const grouped = {};
        infos.forEach((item) => {
          const key = CategoryMap[item.category] || '기타';
          const m = moment(item.registTime * 1000);
          if (m.year() !== selectedYear) return;
          if (!grouped[key]) grouped[key] = Array.from({ length: 12 }, () => 0);
          grouped[key][m.month()] += item.price || 0;
        });

        if (!canceled) setYearData(grouped);
      } catch (e) {
        if (!canceled) setYearData({});
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    fetchYear();
    return () => {
      canceled = true;
    };
  }, [visible, clubID, socketKey, selectedYear]);

  const chartData = useMemo(() => {
    if (!yearData) return [];
    return Object.keys(yearData).map((name) => ({ name, monthly: yearData[name] }));
  }, [yearData]);

  const maxVal = useMemo(() => {
    let max = 0;
    chartData.forEach((c) => c.monthly.forEach((v) => { if (v > max) max = v; }));
    return max || 1;
  }, [chartData]);

  const totals = useMemo(() => {
    const monthTotals = Array.from({ length: 12 }, () => 0);
    let grand = 0;
    chartData.forEach((c) =>
      c.monthly.forEach((v, i) => {
        monthTotals[i] += v;
        grand += v;
      }),
    );
    return { monthTotals, grand };
  }, [chartData]);

  // 판매 요약(파이)용: 카테고리별 연간 합계
  const pieCategory = useMemo(() => {
    return chartData
      .map((c) => ({ name: c.name, price: c.monthly.reduce((a, b) => a + b, 0) }))
      .filter((c) => c.price > 0);
  }, [chartData]);

  if (!visible) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>년매출</Title>
          <YearControl>
            <ArrowBtn onClick={() => setSelectedYear((y) => y - 1)}>
              <MdChevronLeft />
            </ArrowBtn>
            <YearText>{selectedYear}년</YearText>
            <ArrowBtn onClick={() => setSelectedYear((y) => y + 1)}>
              <MdChevronRight />
            </ArrowBtn>
          </YearControl>
          <CloseBtn onClick={onClose}>
            <MdClose />
          </CloseBtn>
        </Header>
        <Body>
          {loading ? (
            <LoadingWrap>
              <Ring />
            </LoadingWrap>
          ) : chartData.length === 0 ? (
            <Empty>{selectedYear}년 매출 데이터가 없습니다.</Empty>
          ) : (
            <>
              <SectionTitle>월별 매출 추이 ({totals.grand.toLocaleString()}원)</SectionTitle>
              <ChartWrapper>
                <ChartLayout>
                  <ChartYAxis>
                    {formatYAxis(maxVal)
                      .slice()
                      .reverse()
                      .map((v) => (
                        <span key={v}>{formatYLabel(v)}</span>
                      ))}
                  </ChartYAxis>
                  <ChartSvg viewBox="0 0 100 40" preserveAspectRatio="none">
                    <defs>
                      {chartData.map((row, idx) => {
                        const color = COLORS[idx % COLORS.length];
                        return (
                          <linearGradient key={row.name} id={`yearLine-${idx}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={color} stopOpacity="1" />
                            <stop offset="100%" stopColor={color} stopOpacity="1" />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    {chartData.map((row, idx) => {
                      const n = 12;
                      const pts = row.monthly.map((v, i) => {
                        const x = (i / (n - 1)) * 100;
                        const y = 35 - (v / maxVal) * 30;
                        return { x, y };
                      });
                      const d = 'M ' + pts.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' L ');
                      return (
                        <path
                          key={row.name}
                          d={d}
                          fill="none"
                          stroke={`url(#yearLine-${idx})`}
                          strokeWidth={0.22}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          opacity={0.9}
                        />
                      );
                    })}
                  </ChartSvg>
                </ChartLayout>
                <ChartLabels>
                  {Array.from({ length: 12 }, (_, i) => (
                    <span key={i}>{i + 1}월</span>
                  ))}
                </ChartLabels>
                <Legend>
                  {chartData.map((row, idx) => (
                    <LegendItem key={row.name}>
                      <LegendDot color={COLORS[idx % COLORS.length]} />
                      <span>{row.name}</span>
                    </LegendItem>
                  ))}
                </Legend>
              </ChartWrapper>

              <SectionTitle>월별 매출 표</SectionTitle>
              <TableScroll>
                <DataTable>
                  <thead>
                    <tr>
                      <th>카테고리</th>
                      {Array.from({ length: 12 }, (_, i) => (
                        <th key={i}>{i + 1}월</th>
                      ))}
                      <th className="total-col">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row) => {
                      const rowTotal = row.monthly.reduce((a, b) => a + b, 0);
                      return (
                        <tr key={row.name}>
                          <td>{row.name}</td>
                          {row.monthly.map((v, i) => (
                            <td key={i} className="num">{v.toLocaleString()}</td>
                          ))}
                          <td className="num total-col">{rowTotal.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td>합계</td>
                      {totals.monthTotals.map((v, i) => (
                        <td key={i} className="num">{v.toLocaleString()}</td>
                      ))}
                      <td className="num total-col">{totals.grand.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </DataTable>
              </TableScroll>

              {pieCategory.length > 0 && (
                <>
                  <SectionTitle style={{ marginTop: 36 }}>판매 요약</SectionTitle>
                  <SummaryWrap>
                    <PieBox>
                      <Chart dynamicValue={{ category: pieCategory }} />
                    </PieBox>
                    <SummaryList>
                      {pieCategory.map((c, idx) => {
                        const pct = totals.grand > 0 ? Math.round((c.price / totals.grand) * 100) : 0;
                        return (
                          <SummaryItem key={c.name}>
                            <LegendDot color={PIE_COLORS[idx % PIE_COLORS.length]} />
                            <span style={{ flex: 1 }}>{c.name}</span>
                            <span>{c.price.toLocaleString()}원 ({pct}%)</span>
                          </SummaryItem>
                        );
                      })}
                      <SummaryItem style={{ fontWeight: 700, borderTop: `1px solid ${palette.webGray[3]}`, marginTop: 4, paddingTop: 8 }}>
                        <span style={{ flex: 1 }}>합계</span>
                        <span>{totals.grand.toLocaleString()}원</span>
                      </SummaryItem>
                    </SummaryList>
                  </SummaryWrap>
                </>
              )}
            </>
          )}
        </Body>
      </ModalBox>
    </Overlay>
  );
};

export default YearSalesModal;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  width: 920px;
  max-width: 94vw;
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid ${palette.webGray[3]};
`;

const Title = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${palette.webBlack};
`;

const YearControl = styled.div`
  display: flex;
  align-items: center;
  margin-left: 24px;
`;

const ArrowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.4rem;
  color: ${palette.webGray[7]};
  &:hover {
    color: ${palette.webCyan[5]};
  }
  &:focus {
    outline: none;
  }
`;

const YearText = styled.div`
  min-width: 64px;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${palette.webBlack};
`;

const CloseBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.4rem;
  color: ${palette.webGray[7]};
  &:hover {
    color: ${palette.webBlack};
  }
  &:focus {
    outline: none;
  }
`;

const Body = styled.div`
  padding: 24px;
  overflow: auto;
`;

const SectionTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${palette.webBlack};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
`;

const Empty = styled.div`
  text-align: center;
  color: ${palette.webGray[6]};
  padding: 80px 0;
  font-size: 0.95rem;
`;

const ChartWrapper = styled.div`
  width: 100%;
  margin: 4px 0 36px 0;
`;

const ChartLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
`;

const ChartYAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px 8px 24px 0;
  font-size: 0.7rem;
  color: ${palette.webGray[6]};
  font-variant-numeric: tabular-nums;
  min-width: 48px;
`;

const ChartSvg = styled.svg`
  flex: 1;
  min-width: 0;
  height: 220px;
  overflow: visible;
`;

const ChartLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 48px;
  font-size: 0.72rem;
  color: ${palette.webGray[6]};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 16px;
  margin-left: 48px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: ${palette.webBlack};
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${(props) => props.color};
`;

const SummaryWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin: 4px 0 36px 0;
  flex-wrap: wrap;
`;

const PieBox = styled.div`
  width: 260px;
  height: 260px;
  flex-shrink: 0;
`;

const SummaryList = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 0.9rem;
  color: ${palette.webBlack};

  span:last-child {
    font-variant-numeric: tabular-nums;
  }
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  white-space: nowrap;

  th,
  td {
    border: 1px solid ${palette.webGray[3]};
    padding: 6px 10px;
    text-align: center;
  }
  th {
    background: ${palette.webGray[1]};
    font-weight: 600;
    color: ${palette.webBlack};
  }
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .total-col {
    background: ${palette.webGray[1]};
    font-weight: 600;
  }
  tr.total-row td {
    background: ${palette.webGray[1]};
    font-weight: 600;
  }
`;
